import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Settings, 
  Bell, 
  Download, 
  Upload, 
  HelpCircle, 
  LogOut,
  User,
  Shield,
  FileText,
  Calendar,
  MessageSquare,
  BarChart3,
  BookOpen,
  Users,
  GraduationCap
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'
import { paths } from '@/routes/path'

interface MenuItem {
  label: string
  href: string
  icon: any
  description: string
  roles?: string[]
  badge?: string
}

const menuItems: MenuItem[] = [
  {
    label: 'Profile Settings',
    href: '/profile',
    icon: User,
    description: 'Manage your personal information and preferences'
  },
  {
    label: 'Notification Settings',
    href: '/notification-settings',
    icon: Bell,
    description: 'Configure how you receive notifications'
  },
  {
    label: 'System Settings',
    href: '/settings',
    icon: Settings,
    description: 'General system preferences and configurations'
  },
  {
    label: 'Security Settings',
    href: '/security',
    icon: Shield,
    description: 'Manage your account security and privacy'
  },
  {
    label: 'Export Data',
    href: '/export',
    icon: Download,
    description: 'Download your data and reports'
  },
  {
    label: 'Upload Resources',
    href: '/upload-resources',
    icon: Upload,
    description: 'Upload course materials and resources'
  },
  {
    label: 'Help Center',
    href: '/help',
    icon: HelpCircle,
    description: 'Get help and support'
  },
  {
    label: 'System Status',
    href: '/status',
    icon: BarChart3,
    description: 'Check system performance and status'
  }
]

const quickActions = [
  {
    label: 'Submit Assignment',
    href: paths.app.assignments.root.absolute,
    icon: FileText,
    description: 'Submit your completed assignments',
    roles: ['student']
  },
  {
    label: 'Create Assignment',
    href: paths.app.assignments.create.absolute,
    icon: FileText,
    description: 'Create new assignments for students',
    roles: ['instructor', 'admin']
  },
  {
    label: 'Schedule Meeting',
    href: '/meetings/schedule',
    icon: Calendar,
    description: 'Schedule virtual meetings with students',
    roles: ['instructor', 'admin']
  },
  {
    label: 'Send Announcement',
    href: '/announcements/create',
    icon: MessageSquare,
    description: 'Send announcements to students',
    roles: ['instructor', 'admin']
  }
]

export function MorePage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setIsLoggingOut(false)
    }
  }

  const filteredMenuItems = menuItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role as any))
  )

  const filteredQuickActions = quickActions.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role as any))
  )

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">More Options</h1>
          <p className="text-sm sm:text-base text-gray-600">Access additional features and settings</p>
        </div>
        <Button
          onClick={handleLogout}
          disabled={isLoggingOut}
          variant="outline"
          className="text-red-600 border-red-200 hover:bg-red-50 w-full sm:w-auto"
        >
          <LogOut className="h-4 w-4 mr-2" />
          {isLoggingOut ? 'Logging out...' : 'Logout'}
        </Button>
      </div>

      {/* Quick Actions */}
      {filteredQuickActions.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {filteredQuickActions.map((action) => (
              <Card 
                key={action.href} 
                className="p-4 hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(action.href)}
              >
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                    <action.icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                      {action.label}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">{action.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Settings Menu */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Settings & Preferences</h2>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filteredMenuItems.map((item) => (
            <Card 
              key={item.href} 
              className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => navigate(item.href)}
            >
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="p-2 sm:p-3 bg-gray-100 rounded-lg group-hover:bg-primary-100 transition-colors">
                  <item.icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 group-hover:text-primary-600 transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm sm:text-base font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                      {item.label}
                    </h3>
                    {item.badge && (
                      <Badge variant="secondary" className="text-xs">
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">{item.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* User Info */}
      <Card className="p-4 sm:p-6 bg-gray-50">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <div className="p-2 sm:p-3 bg-primary-100 rounded-full">
            <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base font-medium text-gray-900 truncate">
              {user?.firstName} {user?.lastName}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{user?.email}</p>
            <Badge variant="outline" className="mt-1 text-xs">
              {user?.role}
            </Badge>
          </div>
        </div>
      </Card>
    </div>
  )
}
